import { useMemo } from 'react'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import InvestmentSectionAccordion from './InvestmentSectionAccordion'
import { useQueries } from '@tanstack/react-query'
import { convertUnitAndValue, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { UNITS } from '../../../types/misc.types'
import { InvestmentDataType, MemberInvestmentEntryType } from '../types/investmentApi.types'
import { InvestmentAccordionDataModel, InvestmentRowModel } from '../types/investmentSection.types'
import { buildPrimaryInvestmentRowModel } from '../utils/buildPrimaryInvestmentRowModel'
import { buildSecondaryInvestmentRowModel } from '../utils/buildSecondaryInvestmentRowModel'
import { fetchTaxLiability } from '../repository/userInvestmentApi'

export default function InvestmentSection() {
  const { isin, investmentData } = useMetaDataContext()
  const members = investmentData?.memberInvestmentData
  const hasMemberList = (members?.length ?? 0) > 0

  function buildInvestmentRows({
    investmentData,
    members,
    hasMemberList
  }: {
    investmentData?: InvestmentDataType
    members?: MemberInvestmentEntryType[]
    hasMemberList: boolean
  }): InvestmentRowModel[] {
    if (!investmentData) return []
    if (hasMemberList) {
      return (members ?? [])
        .filter((m) => (m?.investedAmount?.amount ?? 0) > 0)
        .map((member, index) => buildSecondaryInvestmentRowModel({ member, index }))
    }
    if (!(investmentData?.investedAmount?.amount ?? 0)) return []
    return [buildPrimaryInvestmentRowModel({ investmentData })]
  }

  const rows = useMemo(
    () => buildInvestmentRows({ investmentData, members, hasMemberList }),
    [hasMemberList, investmentData, members]
  )
  const taxLiabilityTokens = useMemo(
    () => Array.from(new Set(rows.map((row) => row.temporaryFamilyAccessToken))),
    [rows]
  )

  useQueries({
    queries: taxLiabilityTokens.map((temporaryFamilyAccessToken) => ({
      queryKey: ['taxLiability', isin, temporaryFamilyAccessToken || undefined],
      queryFn: () => fetchTaxLiability(isin, temporaryFamilyAccessToken || undefined),
      retry: 1,
      enabled: !!isin,
      refetchOnWindowFocus: false
    }))
  })

  if (rows?.length === 0) return null

  return (
    <div className='bg-white'>
      {rows.map((row) => {
        const accordionDataModel = buildInvestmentAccordionDataModel({
          row,
          change: buildChangeTextAndDirectionValue(row.investmentDataOverride)
        })

        return (
          <InvestmentSectionAccordion
            key={row.key}
            isin={isin}
            investmentDataOverride={row.investmentDataOverride}
            temporaryFamilyAccessToken={row.temporaryFamilyAccessToken}
            accordionDataModel={accordionDataModel}
          />
        )
      })}
    </div>
  )
}

function buildInvestmentAccordionDataModel({
  row,
  change
}: {
  row: InvestmentRowModel
  change: {
    changeDirectionValue: number
    changeText: string
  }
}): InvestmentAccordionDataModel {
  return {
    iconInitial: row.iconInitial,
    title: 'Investment',
    titleLeadingText: row.titleLeadingText,
    titleTrailingText: row.titleTrailingText,
    value: `${roundOffAndFormatNumberWithCurrency(row?.investmentDataOverride?.currentGain?.amount ?? 0)}`,
    showSipBadge: (row?.investmentDataOverride?.sipAmount?.amount ?? 0) > 0,
    sipText: `SIP ${roundOffAndFormatNumberWithCurrency(row?.investmentDataOverride?.sipAmount?.amount ?? 0)}`,
    changeDirectionValue: change.changeDirectionValue,
    changeText: change.changeText,
    headerAccordionBgColorClassName: row?.investmentDataOverride?.xirr?.value === 0 ? 'bg-[#F6F9F9]' : 'bg-white'
  }
}

function buildChangeTextAndDirectionValue(investmentData: InvestmentDataType): {
  changeDirectionValue: number
  changeText: string
} {
  if (typeof investmentData?.xirr?.value === 'number') {
    const changeDirectionValue = investmentData?.xirr?.value ?? 0
    return {
      changeDirectionValue,
      changeText: `${convertUnitAndValue({
        value: Math.abs(changeDirectionValue),
        unit: investmentData?.xirr?.unit ?? UNITS.PERCENTAGE
      })} XIRR`
    }
  }

  const changeDirectionValue = investmentData?.fundReturns?.returns?.value ?? 0
  return {
    changeDirectionValue,
    changeText: `${convertUnitAndValue({
      value: Math.abs(changeDirectionValue),
      unit: investmentData?.fundReturns?.returns?.unit ?? UNITS.PERCENTAGE
    })} Returns`
  }
}

